/* eslint-disable import/prefer-default-export */
import {
  computed, ref, unref, type Ref, watch, type UnwrapRef,
} from 'vue';

export function useState<T>(initial: T) {
  const state = ref(initial);
  const setState = (p: T) => {
    if (typeof p === 'function') {
      state.value = p(state.value);
    } else {
      (state.value as T) = p;
    }
  };
  return [state, setState];
}

export function useGuardedRef<T>(source: Ref<T>, guardFn: (data: T) => T) {
  return computed({
    get() {
      return guardFn(source.value);
    },
    set(value) {
      source.value = value;
    },
  });
}

export function syncRef<S>(source: S | Ref<S>): Ref<S>;
export function syncRef<S, T = S>(source: S | Ref<S>, syncFn?: (data: S) => T): Ref<T>;
export function syncRef<S, T = S>(source: S | Ref<S>, syncFn?: (data: S) => T): Ref<T> | Ref<S> {
  const sync: ((data: S) => T) | ((data: S) => S) = syncFn ?? ((d: S) => d);

  const target = ref<T | S>(sync(unref(source)));

  watch(
    () => unref(source),
    (newValue) => {
      target.value = sync(newValue) as UnwrapRef<T>;
    },
  );

  return target as Ref<T> | Ref<S>;
}
